import axios, { type AxiosResponse } from 'axios';
import type { SagaIterator } from 'redux-saga';
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import ConfigurationsAPI from 'js/api/Configuration';
import configActions, {
  actionTypes,
  selectors,
} from '../reducers/Configuration';
import cacheActions, {
  actionTypes as cacheActionTypes,
  selectors as cacheSelectors,
} from '../reducers/Cache';
import { fulfilled, rejected } from '../factories/ApiCall';
import WebRequest from './WebRequest';
import busyActions from 'js/redux/reducers/Busy';
import offlineActions from 'js/redux/reducers/Offline';
import errorActions from 'js/redux/reducers/Errors';
import protectionActions, {
  actionTypes as protectionActionTypes,
  selectors as protectionSelectors,
} from 'js/redux/reducers/Protection';
import orderActions from 'js/redux/reducers/Order';
import inventoryActions from 'js/redux/reducers/Inventory';
import i18n from 'js/localization/i18n';
import loginActions from 'js/redux/reducers/Login';
import userActions, {
  selectors as userSelectors,
} from 'js/redux/reducers/User';
import { actionTypes as machineActionTypes } from 'js/redux/reducers/Machine';
import { actionTypes as updateActionTypes } from 'js/redux/reducers/UpdateChecker';
import _ from 'lodash';
import { LOCATION_CHANGE, push, getLocation } from 'js/redux/Router';
import {
  ADD_TYPE_SPLASH,
  FEATURE_PRO,
  FORMULA_INPUT_CARD,
  ORDER_MODE_FREE_DISPENSE,
  ORDER_MODE_LOCAL_FORMULA,
  ORDER_MODE_MATCHING,
} from 'js/Constants';
import {
  IT3_LANGUAGE_TO_IT4,
  IT4_LANGUAGE_TO_IT3,
} from '../../mylib/LanguageMap';

import CacheAPI from '../../api/Cache';
import updateApi from '../../api/SWUpdateApi';
import { handle_db_name } from '../Utils';
import log from '../../api/Logger';
import { autoLogin } from './Login';
import { setBackgroundImage } from '../../../backgroundImage.js';
import type {
  ClientVersions,
  ConfigurationMap,
  JsonValue,
  LabelTemplate,
  Site,
  Systemgroup,
} from '../../api/Configuration';
import type { InitZoneBundle, Zone } from '../../api/Cache';
import type { ApiThunk } from '../../api/types/WebRequest';
import type {
  ConfigurationAction,
  ConfigurationValues,
} from '../types/Configuration';
import type { CurrentUser, UserRecord } from '../types/User';
import type { LicenseStatusResponse } from '../../api/Protection';

export const MS_IN_MINUTE = 60000;
export const AUTO_RELOAD_INTERVAL = 60 * MS_IN_MINUTE;
export const AUTO_RELOAD_DELAY = 5 * MS_IN_MINUTE;
export const FORCED_AUTO_RELOAD_INTERVAL = 90 * MS_IN_MINUTE;

type PayloadAction<Payload> = ConfigurationAction<Payload> & {
  payload: Payload;
};

type ConfigChangePayload = { key: string; value?: JsonValue };
type ConfigurationBundle = { configuration: ConfigurationMap };
type ApiFunction<Params, Data> = (params: Params) => ApiThunk<Data>;
type SplashAdvertisement = Zone['advertisement'][number] & {
  md5_hash?: string | null;
};

function callQtBridge(method: 'setConfig' | 'setSplashImage', value: string) {
  const bridge = window.qtside?.bridge;
  const bridgeMethod = bridge?.[method];
  if (!bridge || typeof bridgeMethod !== 'function') {
    throw new TypeError(`Qt bridge ${method} is not available`);
  }
  bridgeMethod.call(bridge, value);
}

function getHttpStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

function* updateShotformatters(): SagaIterator<void> {
  // Setting shotformater
  try {
    const config_values = yield call(waitForConfigValues);
    const unitname = config_values.formula_display_unit;
    const unit = yield select(cacheSelectors.getUnit, unitname);
    if (unit) {
      yield put({ type: actionTypes.SET_SHOTFORMATTER_UNIT, payload: unit });
    }
  } catch (e) {
    log.error(e);
  }
}

function* loadConfigurations(
  action?: ConfigurationAction<{ bundle?: ConfigurationBundle }>
): SagaIterator<void> {
  const { zoneid } = yield call(waitForZone);
  const { siteid } = yield call(waitForSite);
  const is_pro = yield call(waitForIsPro);

  try {
    let payload: ConfigurationMap;
    if (action?.payload?.bundle) {
      payload = action.payload.bundle.configuration;
    } else {
      yield put({ type: actionTypes.FETCH_CONFIG_PENDING });
      const response: AxiosResponse<ConfigurationMap> = yield call(
        WebRequest,
        ConfigurationsAPI.loadConfigurations(zoneid, siteid)
      );
      payload = response.data;
    }
    // set value property from site_value or common_value
    for (let obj of Object.values(payload)) {
      obj.value =
        obj.site_value === undefined ? obj.common_value : obj.site_value;
    }

    // update language to correct one based on IT3_LANGUAGE_TO_IT4 map
    const languageValue = payload.language?.value;
    if (
      typeof languageValue === 'string' &&
      IT3_LANGUAGE_TO_IT4.has(languageValue)
    ) {
      payload.language.value = IT3_LANGUAGE_TO_IT4.get(languageValue);
    }

    if (!is_pro) {
      const in_use = String(payload.home_buttons?.value).split(';');
      const pro_only = ['article', 'matching', 'customer'];
      const value = _.difference(in_use, pro_only).join(';');
      payload['home_buttons'] = {
        ...payload.home_buttons,
        value,
      };
    }
    // send returned object back to reducer as payload:
    yield put({ type: actionTypes.FETCH_CONFIG_FULFILLED, payload: payload });
    yield put({ type: machineActionTypes.FETCH_MACHINES_LIST, payload: null });
    yield put({ type: actionTypes.UPDATE_SHOTFORMATTER });

    // Send config to QtSide as well
    if (window.qtside) {
      callQtBridge('setConfig', JSON.stringify(payload));
    }
    yield call(syncUpdatePolling);
  } catch (e) {
    yield put({ type: actionTypes.FETCH_CONFIG_REJECTED, payload: e });
    yield put(
      errorActions.showCriticalError(
        i18n.t('msg.unableToLoadConfiguration', 'Unable to load configuration'),
        e
      )
    );
  }
}

function* loadPrintLabels(): SagaIterator<void> {
  try {
    const { zoneid } = yield call(waitForZone);
    const { siteid } = yield call(waitForSite);
    const response: AxiosResponse<LabelTemplate[]> = yield call(
      WebRequest,
      ConfigurationsAPI.loadPrintLabels(zoneid, siteid)
    );
    yield put(configActions.setPrintLabels(response.data));
  } catch (e) {
    yield put(
      errorActions.showCriticalError(
        i18n.t('msg.unableToLoadPrintLabels', 'Unable to load print labels'),
        e
      )
    );
  }
}
function* loadEmailTemplates(): SagaIterator<void> {
  try {
    const { zoneid } = yield call(waitForZone);
    const { siteid } = yield call(waitForSite);
    const response: AxiosResponse<LabelTemplate[]> = yield call(
      WebRequest,
      ConfigurationsAPI.loadEmailTemplates(zoneid, siteid)
    );
    yield put(configActions.setEmailTemplates(response.data));
  } catch (e) {
    yield put(
      errorActions.showCriticalError(
        i18n.t(
          'msg.unableToLoadEmailTemplates',
          'Unable to load email templates'
        ),
        e
      )
    );
  }
}

function* savePrintLabels(
  action: PayloadAction<LabelTemplate[]>
): SagaIterator<void> {
  try {
    const { siteid } = yield call(waitForSite);
    const response: AxiosResponse<LabelTemplate[]> = yield call(
      WebRequest,
      ConfigurationsAPI.savePrintLabels(siteid, action.payload)
    );
    const site_labels = response.data; // these have true id from db
    const labels: LabelTemplate[] = yield select(selectors.print_labels);
    const zone_labels = labels.filter((x) => x.zoneid != null);
    yield put(configActions.setPrintLabels([...zone_labels, ...site_labels]));
  } catch (e) {
    yield put(
      errorActions.showCriticalError(
        i18n.t('msg.unableToSetConfiguration', 'Unable to set configuration'),
        e
      )
    );
  }
}

function* saveEmailTemplates(
  action: PayloadAction<LabelTemplate[]>
): SagaIterator<void> {
  try {
    const { siteid } = yield call(waitForSite);
    const response: AxiosResponse<LabelTemplate[]> = yield call(
      WebRequest,
      ConfigurationsAPI.saveEmailTemplates(siteid, action.payload)
    );
    const site_templates = response.data; // these have true id from db
    const templates: LabelTemplate[] = yield select(selectors.email_templates);
    const zone_templates = templates.filter((x) => x.zoneid != null);
    yield put(
      configActions.setEmailTemplates([...zone_templates, ...site_templates])
    );
  } catch (e) {
    yield put(
      errorActions.showCriticalError(
        i18n.t('msg.unableToSetConfiguration', 'Unable to set configuration'),
        e
      )
    );
  }
}

function* saveSiteDetails(action: PayloadAction<Site>): SagaIterator<void> {
  // update site data
  try {
    yield call(WebRequest, ConfigurationsAPI.updateSite(action.payload));
    yield put(configActions.setSite(action.payload));
  } catch (e) {
    yield put({ type: actionTypes.UPDATE_SITE_REJECTED, payload: e });
    yield put(
      errorActions.showCriticalError(
        i18n.t(
          'msg.unableToUpdateSiteDetails',
          'Unable to update site details'
        ),
        e
      )
    );
  }
}

function* setConfiguration(
  action: PayloadAction<Required<ConfigChangePayload>>
): SagaIterator<void> {
  const config: ConfigurationMap = yield select(selectors.config);
  const { zoneid } = yield call(waitForZone);
  const { siteid } = yield call(waitForSite);
  try {
    const { key, value } = action.payload;
    const syncUpdatePolicyAfterSave = key === 'automatic_updates_logic';
    const value_object = { ...config[key] };

    // Do not update if not changed
    if (_.isEqual(value_object.value, value)) {
      return;
    }

    // transform values for server
    let dbvalue = value;
    if (key === 'language') {
      if (typeof value !== 'string') return;
      const translatedValue = IT4_LANGUAGE_TO_IT3.get(value);
      if (translatedValue === undefined) return;
      dbvalue = translatedValue;
    }

    yield put(configActions.savingConfig(true));

    // update redux
    value_object.value = value;
    value_object.site_value = value;
    yield put({
      type: actionTypes.SET_CONFIG_VALUE_REDUX,
      payload: { key, object: value_object },
    });

    // Send config to QtSide as well
    if (window.qtside && !syncUpdatePolicyAfterSave) {
      const updated_config = yield select(selectors.config);
      callQtBridge('setConfig', JSON.stringify(updated_config));
    }
    // update server
    yield call(
      WebRequest,
      ConfigurationsAPI.setSiteConfiguration(zoneid, siteid, key, dbvalue)
    );

    if (window.qtside && syncUpdatePolicyAfterSave) {
      const updated_config = yield select(selectors.config);
      callQtBridge('setConfig', JSON.stringify(updated_config));
    }

    yield put(configActions.savingConfig(false));

    yield call(afterConfigChange, action);
  } catch (e) {
    yield put(
      errorActions.showCriticalError(
        i18n.t('msg.unableToSetConfiguration', 'Unable to set configuration'),
        e
      )
    );
    // restore previous config
    yield put({ type: actionTypes.FETCH_CONFIG_FULFILLED, payload: config });
  }
}

function* unsetConfiguration(
  action: PayloadAction<Pick<ConfigChangePayload, 'key'>>
): SagaIterator<void> {
  const config: ConfigurationMap = yield select(selectors.config);
  const { zoneid } = yield call(waitForZone);
  const { siteid } = yield call(waitForSite);
  try {
    const { key } = action.payload;
    const syncUpdatePolicyAfterSave = key === 'automatic_updates_logic';

    const { site_value, ...value_object } = config[key]; // excludes site_value

    if (
      key === 'language' &&
      typeof value_object.common_value === 'string' &&
      IT3_LANGUAGE_TO_IT4.has(value_object.common_value)
    ) {
      value_object.value = IT3_LANGUAGE_TO_IT4.get(value_object.common_value);
    } else {
      value_object.value = value_object.common_value;
    }

    yield put(configActions.savingConfig(true));

    // update redux
    yield put({
      type: actionTypes.SET_CONFIG_VALUE_REDUX,
      payload: { key, object: value_object },
    });

    // Send config to QtSide as well
    if (window.qtside && !syncUpdatePolicyAfterSave) {
      const updated_config = yield select(selectors.config);
      callQtBridge('setConfig', JSON.stringify(updated_config));
    }
    // update server
    yield call(
      WebRequest,
      ConfigurationsAPI.unsetSiteConfiguration(zoneid, siteid, key)
    );

    if (window.qtside && syncUpdatePolicyAfterSave) {
      const updated_config = yield select(selectors.config);
      callQtBridge('setConfig', JSON.stringify(updated_config));
    }

    yield put(configActions.savingConfig(false));

    yield call(afterConfigChange, action);
  } catch (e) {
    yield put(
      errorActions.showCriticalError(
        i18n.t('msg.unableToSetConfiguration', 'Unable to set configuration'),
        e
      )
    );
    // restore previous config
    yield put({ type: actionTypes.FETCH_CONFIG_FULFILLED, payload: config });
  }
}

function* afterConfigChange(
  action: PayloadAction<ConfigChangePayload>
): SagaIterator<void> {
  try {
    const { key } = action.payload;
    // notify other sagas
    if (key === 'formula_display_unit') {
      // Update only if really needed
      yield put({ type: actionTypes.UPDATE_SHOTFORMATTER });
    } else if (key === 'automatic_updates_logic') {
      yield call(syncUpdatePolling);
    }
  } catch (e) {
    log.error(e);
  }
}

function* syncUpdatePolling(): SagaIterator<void> {
  const configValues: ConfigurationValues = yield select(
    selectors.config_values
  );
  yield put({ type: updateActionTypes.POLL_STOP });
  if (Number(configValues.automatic_updates_logic ?? 2) !== 0) {
    // Restart both update workers so the effective policy is applied after
    // setting changes as well as site and zone configuration reloads.
    yield put({ type: updateActionTypes.POLL_START });
  }
}

function* setZone(action: PayloadAction<Zone>): SagaIterator<void> {
  // fire load zone related data actions!
  // only dispatch actions if zone is available since they are all null by default
  if (action.payload) {
    yield put({
      type: cacheActionTypes.TOGGLE_PRIORITY_INIT_PENDING,
      payload: true,
    });
    const currentLocalUser: CurrentUser = yield select(
      userSelectors.current_user
    );
    const users: UserRecord[] = yield select(userSelectors.users);
    yield put({ type: actionTypes.SET_ZONE_REDUX, payload: action.payload });

    // Fetching site users
    yield put(userActions.fetchSiteUsers());
    // Login quest account, null username and null password if current user is not a local user.
    if (
      users.length === 0 ||
      currentLocalUser.stduser ||
      !currentLocalUser.username
    ) {
      yield put(userActions.loginUser());
    }

    yield call(loadConfigurations);
    yield call(loadInitZoneBundle);

    yield put({
      type: cacheActionTypes.TOGGLE_PRIORITY_INIT_PENDING,
      payload: false,
    });

    // low priority fetches
    yield call(loadPrintLabels);
    yield call(loadEmailTemplates);
    yield put(cacheActions.loadCommentsList(action.payload.zoneid));

    // set background image
    setBackgroundImage(action.payload.zoneid);

    yield put({
      type: actionTypes.SET_ZONE_FULLFILLED,
    });
  }
}

function* loadInitZoneBundle(): SagaIterator<void> {
  try {
    const zone = yield call(waitForZone);
    const site = yield call(waitForSite);

    yield put({ type: cacheActionTypes.FETCH_PRODUCTS_PENDING });
    yield put({ type: cacheActionTypes.FETCH_PRODUCT_GROUPS_PENDING });
    yield put({ type: cacheActionTypes.FETCH_CARDS_PENDING });
    yield put({ type: cacheActionTypes.FETCH_UNITS_PENDING });
    yield put({ type: cacheActionTypes.FETCH_CNTS_PENDING });

    const { data }: AxiosResponse<InitZoneBundle> = yield call(
      WebRequest,
      CacheAPI.loadInitZoneBundle(zone.zoneid, site.siteid, site.systemgroupid)
    );

    yield put(cacheActions.loadProducts(data));
    yield put(cacheActions.loadCards(data));
    yield put(cacheActions.loadUnits(data));
    yield put(cacheActions.loadColorants(data));
  } catch (e) {
    yield put(
      errorActions.showCriticalError(
        i18n.t('msg.unableToLoadConfiguration', 'Unable to load configuration'),
        e
      )
    );
  }
}

// return selected zone, waiting for SET_ZONE if necessary
export function* waitForZone(): SagaIterator<Zone> {
  let zone: Zone | null | undefined = yield select(selectors.zone);
  while (!zone) {
    const action: ConfigurationAction<Zone> = yield take(actionTypes.SET_ZONE);
    zone = action.payload;
  }
  return zone;
}

// return config, waiting for FETCH_CONFIG_FULFILLED if necessary
export function* waitForConfig(): SagaIterator<ConfigurationMap> {
  let config: ConfigurationMap | undefined = yield select(selectors.config);
  while (_.isEmpty(config)) {
    const action: ConfigurationAction<ConfigurationMap> = yield take(
      actionTypes.FETCH_CONFIG_FULFILLED
    );
    config = action.payload;
  }
  return config;
}

// return config_values, waiting for FETCH_CONFIG_FULFILLED if necessary
export function* waitForConfigValues(): SagaIterator<ConfigurationValues> {
  let config: ConfigurationValues = yield select(selectors.config_values);
  while (_.isEmpty(config)) {
    yield take(actionTypes.FETCH_CONFIG_FULFILLED);
    config = yield select(selectors.config_values);
  }
  return config;
}

// return selected site, waiting for SET_SITE if necessary
export function* waitForSite(): SagaIterator<Site> {
  let site: Site | null | undefined = yield select(selectors.site);
  while (!site) {
    const action: ConfigurationAction<Site> = yield take(actionTypes.SET_SITE);
    site = action.payload;
  }
  return site;
}

// return selected site, waiting for SET_SITE if necessary
export function* waitForIsPro(): SagaIterator<boolean> {
  let status: LicenseStatusResponse | null | undefined = yield select(
    protectionSelectors.status
  );
  while (!status) {
    const action: ConfigurationAction<LicenseStatusResponse> = yield take(
      protectionActionTypes.FETCH_STATUS_FULLFILLED
    );
    status = action.payload;
  }
  return status.features.includes(FEATURE_PRO);
}

export function* fetchWithSiteid<Params extends object, Data = unknown>(
  apifunc: ApiFunction<Params & { siteid: number }, Data>,
  action: ConfigurationAction<Params>
): SagaIterator<void> {
  const { siteid } = yield call(waitForSite);
  try {
    const params = { ...action.payload, siteid } as Params & { siteid: number };
    const { data } = yield call(WebRequest, apifunc(params));
    yield put({ type: fulfilled(action.type), payload: data });
  } catch (e) {
    yield put({ type: rejected(action.type), payload: e });
  }
}

export function* fetchWithZoneid<Params extends object, Data = unknown>(
  apifunc: ApiFunction<Params & { zoneid: number }, Data>,
  action: ConfigurationAction<Params>
): SagaIterator<void> {
  const { zoneid } = yield call(waitForZone);
  try {
    const params = { ...action.payload, zoneid } as Params & { zoneid: number };
    const { data } = yield call(WebRequest, apifunc(params));
    yield put({ type: fulfilled(action.type), payload: data });
  } catch (e) {
    yield put({ type: rejected(action.type), payload: e });
  }
}

export function* saveWithSiteid<Params extends object, Data = unknown>(
  savefunc: ApiFunction<Params & { siteid: number }, Data>,
  action: ConfigurationAction<Params>
): SagaIterator<Data | undefined> {
  const { siteid } = yield select(selectors.site);
  try {
    if (siteid == null) throw new Error('Site not set');
    const obj = { ...action.payload, siteid } as Params & { siteid: number };
    const { data } = yield call(WebRequest, savefunc(obj));
    yield put({ type: fulfilled(action.type), payload: data });
    return data;
  } catch (e) {
    yield put({ type: rejected(action.type), payload: e });
  }
}

export function* deleteWithSiteid<Params extends object>(
  deleteFunc: ApiFunction<Params & { siteid: number }, { deleted: boolean }>,
  action: ConfigurationAction<Params>
): SagaIterator<void> {
  const { siteid } = yield select(selectors.site);
  try {
    const params = { ...action.payload, siteid } as Params & { siteid: number };
    const { data } = yield call(WebRequest, deleteFunc(params));
    yield put(busyActions.setBusy(true));
    if (data.deleted) {
      yield put({ type: fulfilled(action.type), payload: data });
      yield put(busyActions.setBusy(false));
    }
  } catch (e) {
    yield put({ type: rejected(action.type), payload: e });
    yield put(busyActions.setBusy(false));
  }
}

function* initApp(
  action: PayloadAction<{ first_init?: boolean }>
): SagaIterator<void> {
  // Notify busy
  try {
    yield put(busyActions.setBusy(true));

    if (action.payload.first_init) {
      yield call(autoLogin);
    }

    // Fetching protection status
    yield put(protectionActions.fetchStatus());

    // Clear previous values
    yield put(errorActions.clearError());
    yield put(configActions.setSite(null));

    yield put(cacheActions.loadZones());

    yield put(cacheActions.loadLanguages());

    let versions: ClientVersions | null = null;

    if (window.qtside) {
      yield call(() => updateApi.get_sw_versions());

      const {
        vers,
      }: {
        vers?: ConfigurationAction<ClientVersions>;
        timeout?: true;
      } = yield race({
        vers: take(updateActionTypes.SW_VERSIONS),
        timeout: delay(20 * 1000),
      });
      versions = vers?.payload ?? null;
    }

    // Load site to be used
    const clientname =
      (window as Window & { computername?: string }).computername ||
      'webbrowser';

    try {
      const response: AxiosResponse<Site> = yield call(
        WebRequest,
        ConfigurationsAPI.loadSite(clientname, versions)
      );
      yield put(configActions.setSite(response.data));
    } catch (e) {
      if (getHttpStatus(e) === 404) {
        yield put(
          errorActions.showCriticalError(
            i18n.t(
              'msg.notSiteAccount',
              'User used for login is not connected to any site. Please verify username from resource manager.'
            ),
            null
          )
        );
        yield delay(10000);
        yield put(errorActions.clearError());
        yield put(loginActions.logOutSite());
        return;
      } else {
        throw e;
      }
    }

    // waiting zone to be set and config to be loaded
    const zone: Zone = yield call(waitForZone);
    yield call(waitForConfig);

    // In case of reloading matching, set section order & order mode correctly
    const { pathname }: { pathname: string } = yield select(getLocation);

    // Notify QtSide about the SplashImage
    if (window.qtside) {
      try {
        const ad = zone.advertisement.filter(
          (x) => x.adtype === ADD_TYPE_SPLASH
        ) as SplashAdvertisement[];

        if (ad.length === 0) {
          callQtBridge('setSplashImage', '');
        } else {
          callQtBridge(
            'setSplashImage',
            window.location.origin +
              handle_db_name(
                `/rest/advertisement/${ad[0].adid}/data?hash${ad[0].md5_hash}`
              )
          );
        }
      } catch (e) {
        log.error(e);
        log.error('Not supported to set splash image');
      }
    }

    switch (pathname) {
      case '/matching': {
        yield take(cacheActionTypes.FETCH_PRODUCT_GROUPS_FULFILLED);
        yield put(orderActions.setOrderMode(ORDER_MODE_MATCHING));
        break;
      }
      case '/freedispense': {
        yield put(orderActions.setOrderMode(ORDER_MODE_FREE_DISPENSE));
        yield put(orderActions.setOpenSection(FORMULA_INPUT_CARD));
        break;
      }
      case '/localformula': {
        yield take(cacheActionTypes.FETCH_PRODUCT_GROUPS_FULFILLED);
        yield put(orderActions.setOrderMode(ORDER_MODE_LOCAL_FORMULA));
        break;
      }
    }

    // Notify busy
    const config_values = yield call(waitForConfigValues);
    yield put(busyActions.setBusy(false));

    // Wait for init_zone bundle to load
    yield call(waitForPriorityInit);

    // Load inventory
    if (config_values.enable_warehousing_v4) {
      yield put(inventoryActions.fetchInventory());
    }

    // Load system groups
    const group_response: AxiosResponse<Systemgroup[]> = yield call(
      WebRequest,
      ConfigurationsAPI.getSystemgroup()
    );

    yield put(
      configActions.setSystemgroups(group_response ? group_response.data : [])
    );

    // Offline time left
    yield put(offlineActions.getOfflineTimeRemaining());

    // Wait till machine colorants is fetched to check for warning batch
    yield take(machineActionTypes.SET_MACHINE_COLORANTS);

    // Check warning batch at the start
    yield put({ type: machineActionTypes.CHECK_WARNING_BATCH });
  } catch (e) {
    // Notify busy
    yield put(busyActions.setBusy(false));
    if (getHttpStatus(e) === 403) {
      // Forbidden -->
      // yield put(errorActions.showCriticalError(i18n.t('msg.permissionDenied', 'Permission denied'), e));
      // Redirect to protection page?
      yield put(push('/protection'));
    } else if (getHttpStatus(e) === 423) {
      yield put(
        errorActions.showCriticalError(
          i18n.t(
            'msg.ihqConnectionFailed',
            'Connection to Innovatint HQ has failed! Please verify Innovatint HQ settings in resource manager.'
          ),
          e
        )
      );
    } else {
      yield put(
        errorActions.showCriticalError(
          i18n.t(
            'msg.unableToInitializeApplication',
            'Unable to initialize application. Check connection to server.'
          ),
          e
        )
      );
      // once per 20 seconds check connection to server again
      yield delay(20000);
      yield put(errorActions.clearError());
      yield put(configActions.initializeApp());
    }
  }
}

/** Reload cached data periodically when at home page.
 * Wait after entering home, unless FORCED_AUTO_RELOAD_INTERVAL has already elapsed.
 */
function* autoReload(): SagaIterator<void> {
  const last_load_time = Date.now();
  let elapsed = 0;

  while (true) {
    const { pathname } = yield select(getLocation);
    if (pathname === '/') {
      if (elapsed > FORCED_AUTO_RELOAD_INTERVAL) {
        break;
      }
      const { timeout } = yield race({
        navigation: take(LOCATION_CHANGE),
        timeout: delay(
          Math.max(AUTO_RELOAD_DELAY, AUTO_RELOAD_INTERVAL - elapsed)
        ),
      });
      if (timeout) {
        break;
      }
    } else {
      yield take(LOCATION_CHANGE);
    }
    elapsed = Date.now() - last_load_time;
  }

  yield put(configActions.initializeApp());
}

export function* checkZones(
  action: PayloadAction<{ data: Zone[] }>
): SagaIterator<void> {
  try {
    if (action.payload.data.length === 0) {
      // Inform user about no zones defined!
      yield put(
        errorActions.showCriticalError(
          i18n.t(
            'msg.noZonesCheckConfiguration',
            'No zones defined for this site! Please check configuration in Resource Manager.'
          ),
          null
        )
      );
      yield delay(10000);
      yield put(loginActions.logOutSite());
    }
  } catch (e) {
    log.error(e);
  }
}

// This function is here to avoid cyclic dependency between Cache and Configuration sagas
export function* waitForPriorityInit(): SagaIterator<void> {
  const flag: boolean = yield select(cacheSelectors.priority_init_pending);
  if (flag) {
    yield take(cacheActionTypes.TOGGLE_PRIORITY_INIT_PENDING);
  }
}

export default function* saga(): SagaIterator<void> {
  yield all([
    takeLatest(actionTypes.INITIALIZE_APP, initApp),
    takeLatest(actionTypes.INITIALIZE_APP, autoReload),
    takeLatest(actionTypes.FETCH_CONFIG, loadConfigurations),
    takeLatest(actionTypes.FETCH_CONFIG, loadPrintLabels),
    takeLatest(actionTypes.FETCH_CONFIG, loadEmailTemplates),
    takeEvery(actionTypes.SAVE_PRINT_LABELS, savePrintLabels),
    takeEvery(actionTypes.SAVE_EMAIL_TEMPLATES, saveEmailTemplates),
    takeLatest(actionTypes.SET_ZONE, setZone),
    takeEvery(actionTypes.SET_CONFIG_VALUE, setConfiguration),
    takeEvery(actionTypes.UNSET_CONFIG_VALUE, unsetConfiguration),
    takeLatest(actionTypes.UPDATE_SHOTFORMATTER, updateShotformatters),
    takeLatest(actionTypes.UPDATE_SITE, saveSiteDetails),
    takeLatest(cacheActionTypes.FETCH_ZONES_FULFILLED, checkZones),
    takeLatest(
      actionTypes.FETCH_MATCHSETTING_NAMES,
      fetchWithZoneid,
      ConfigurationsAPI.matchsettingNames
    ),
  ]);
}
